import Konva from 'konva';
import React, { memo } from 'react';
import { Group, Rect } from 'react-konva';

import { toTzMoment } from '../../../shared/lib';
import CanvasTableCapacity, { getCanvasTableCapacityLayout } from './canvasTableCapacity';
import CanvasTableNumber from './canvasTableNumber';
import CanvasTableWarningBar from './canvasTableWarningBar';
import TableSize = GM.ReservesSchemas.TableSize;

interface CanvasTableProps {
  nextReserveItemDate?: string;
  schemaTable: GM.ReservesSchemas.TableDTO;
  onClick?: () => void;
  shadow?: boolean;
  personImage: HTMLImageElement;
  colors: GM.ReservesSchemas.TableStateColorSet;
  restaurantTz: string;
  // isMobile?: boolean;
}

export const CanvasTable = memo(function CanvasTable({
  nextReserveItemDate,
  schemaTable,
  onClick,
  personImage,
  shadow = true,
  colors,
  restaurantTz,
  // isMobile = false,
}: CanvasTableProps) {
  const { table } = schemaTable;
  const { width, height, x, y } = schemaTable;

  // Вычисление ширины стола
  // // @todo Нужно ли применить туже логику для высоты?
  // const scaledWidth = width * scale;
  // const scaledHeight = height * scale;
  const scaledWidth = width;
  const scaledHeight = height;
  let tableSize: TableSize;

  const minSize = 30;
  // let resizeDown = false;
  // let finalWidth = scaledWidth;
  let finalWidth = width;
  // let finalHeight = scaledHeight;
  let finalHeight = height;
  let finalX = x;
  let finalY = y;

  if (scaledWidth < minSize) {
    // Отступаем влево на половину недостающего размера, чтобы плашка была по центру
    finalX -= (minSize - scaledWidth) / 2;
    finalWidth = minSize;
    // resizeDown = true;
  }

  if (scaledHeight < minSize) {
    // Отступаем вверх на половину недостающего размера, чтобы плашка была по центру
    finalY -= (minSize - scaledHeight) / 2;
    finalHeight = minSize;
  }

  const tableBorderWidth = 1;
  let tableBorderRadius = 6;

  if (finalWidth < 50) {
    // Small: min 30x30 и скелится, если меньше; max 49x49
    tableSize = 'S';
    tableBorderRadius = 4;
  } else if (finalWidth < 120) {
    // Medium: min 50x50; max 119x119
    tableSize = 'M';
  } else {
    // Large: min 120x120 и скелится, если больше
    tableSize = 'L';
  }

  // TODO Должен ли capacityHeight учитывать размер рамки?
  const { containerHeight: capacityHeight } = getCanvasTableCapacityLayout(
    tableSize,
    table.capacity.toString(),
    { width: finalWidth },
  );

  const handleMouseOver = (e: Konva.KonvaEventObject<MouseEvent>) => {
    e.target.getStage().container().style.cursor = 'pointer';
  };

  const handleMouseOut = (e: Konva.KonvaEventObject<MouseEvent>) => {
    e.target.getStage().container().style.cursor = 'default';
  };

  const handleClick = (e: Konva.KonvaEventObject<MouseEvent>) => {
    e.cancelBubble = true;
    if (onClick) {
      onClick();
    }
  };

  let minutesLeft: number | undefined = undefined;
  if (nextReserveItemDate) {
    const nextDate = toTzMoment(nextReserveItemDate, restaurantTz);
    minutesLeft = nextDate.diff(toTzMoment(undefined, restaurantTz), 'minutes');
    if (minutesLeft < 0) {
      minutesLeft = 0;
    }
  }

  // // Расширяем hitbox на мобильных для удобства тапа
  // const hitboxPadding = isMobile ? 12 : 0;
  //
  // // Мемоизируем hitbox функцию для избежания создания новой функции при каждом render
  // const hitboxFunc = useMemo(() => {
  //   if (!isMobile || hitboxPadding <= 0) {
  //     return undefined;
  //   }
  //
  //   // Создаем функцию один раз и переиспользуем её
  //   return (context: Konva.Context, shape: Konva.Shape) => {
  //     context.beginPath();
  //     context.rect(
  //       -hitboxPadding,
  //       -hitboxPadding,
  //       finalWidth + hitboxPadding * 2,
  //       finalHeight + hitboxPadding * 2,
  //     );
  //     context.fillStrokeShape(shape);
  //   };
  // }, [isMobile, hitboxPadding, finalWidth, finalHeight]);

  return (
    <Group
      x={finalX + 1}
      y={finalY + 1}
      onMouseOver={onClick && handleMouseOver}
      onMouseOut={onClick && handleMouseOut}
      onClick={handleClick}
      onTap={handleClick}
      // hitFunc={hitboxFunc}
    >
      {/* Общий контур, прямоугольник стола */}
      <Rect
        width={finalWidth}
        height={finalHeight}
        fill={colors.frame.bg}
        stroke={colors.frame.border}
        strokeWidth={tableBorderWidth}
        cornerRadius={tableBorderRadius}
        // Оптимизация: отключаем тени на мобильных для улучшения производительности
        // Тени Konva могут быть дорогими для большого количества столов
        // shadowEnabled={shadow && !isMobile}
        shadowEnabled={shadow}
        shadowColor="rgba(30, 36, 47, 0.2)"
        shadowBlur={12}
        shadowOffset={{ x: 0, y: 4 }}
      />

      {/* Прямоугольник со статусом времени (величина закраски) */}
      {colors.timeBar && (
        <CanvasTableWarningBar
          minutesLeft={minutesLeft}
          container={{
            width: finalWidth,
            height: finalHeight,
            offsetY: capacityHeight,
            // borderWidth: isMobile ? Math.max(tableBorderWidth, 2) : tableBorderWidth,
            borderWidth: tableBorderWidth,
            borderRadius: tableBorderRadius,
          }}
          colors={colors}
          // isMobile={isMobile}
        />
      )}

      {/* Вместимость стола (шапка) */}
      <CanvasTableCapacity
        capacity={table.capacity}
        tableSize={tableSize}
        container={{
          width: finalWidth,
          borderWidth: tableBorderWidth,
          borderRadius: tableBorderRadius,
        }}
        personImage={personImage}
        colors={colors}
      />

      {/* Номер стола */}
      <CanvasTableNumber
        number={table.number}
        container={{
          width: finalWidth,
          height: finalHeight,
          offsetY: capacityHeight,
        }}
        tableSize={tableSize}
        hasNextReserve={!!nextReserveItemDate}
        colors={colors}
        // isMobile={isMobile}
      />
    </Group>
  );
});
